import router from '/src/router';
import {computed, ref, toRefs} from 'vue';
import {SUBSCRIPTION_FAILED_STATES} from '/src/const';
import {useAppSession} from '/src/core/app-session';
import {detectProfitwellBlock} from '/src/core/adblock-detector';
import {openRetainCancellationFlow} from '/src/core/paddle';
import {logEvent} from '/src/logger';
import {useNotifications, type NotificationPublisher} from '/src/core/notifications';

export const NAVBAR_TAGLINE = 'Embedded GUI design toolkit';

export type TSubscriptionProvider = 'paddle' | 'lemonsqueezy';
export type TNavbarCancellationResult = 'paddle-retain' | 'lemonsqueezy-modal' | 'warning';

type TNavbarUser = {
    email?: string | null;
    user_metadata?: {
        full_name?: string | null;
        email?: string | null;
    };
};

type TNavbarAuth = {
    user: TNavbarUser;
} | null;

type TNavbarSubscription = {
    customer_id?: string | null;
    sub_id?: string | null;
    status?: string | null;
};

type TNavbarRouter = {
    push: (path: string) => unknown;
};

type TNavbarSession = ReturnType<typeof useAppSession>;

type TNavbarDependencies = {
    session?: TNavbarSession;
    notifications?: NotificationPublisher;
    router?: TNavbarRouter;
    windowRef?: Window;
    logEvent?: typeof logEvent;
    detectProfitwellBlock?: typeof detectProfitwellBlock;
    openRetainCancellationFlow?: typeof openRetainCancellationFlow;
    getPaddlePortal?: (customerId: string) => Promise<any>;
    signOut?: () => Promise<void>;
};

type TNavbarCancellationOptions = {
    subscription: TNavbarSubscription | null | undefined;
    addWarning: (message: string) => void;
    openLemonSqueezyModal: () => void;
    detectProfitwellBlock: typeof detectProfitwellBlock;
    openRetainCancellationFlow: typeof openRetainCancellationFlow;
};

export const PADDLE_BLOCKED_MESSAGE =
    'Subscription cancellation is blocked by your ad blocker. Please disable it for lopaka.app and try again.';
export const PADDLE_CANCEL_UNAVAILABLE_MESSAGE =
    'Unable to open Paddle cancellation flow. Please try again or contact support.';
export const CANCEL_UNAVAILABLE_MESSAGE = 'Unable to open subscription cancellation. Please contact support.';

export function getNavbarMainLink(auth: TNavbarAuth): string {
    return auth ? '/projects' : '/';
}

export function getNavbarReferralButtonText(isSubscriptionActive: boolean): 'Invite a Friend' | 'Get a free Plus' {
    return isSubscriptionActive ? 'Invite a Friend' : 'Get a free Plus';
}

export function getNavbarAvatarText(auth: TNavbarAuth): string {
    const fullName = auth?.user.user_metadata?.full_name;
    if (fullName) return fullName.slice(0, 2);

    const email = auth?.user.user_metadata?.email || auth?.user.email;
    return email ? email.slice(0, 2) : '';
}

export function isNavbarSubscriptionActive(subscription: TNavbarSubscription | null | undefined): boolean {
    return !!subscription && !SUBSCRIPTION_FAILED_STATES.includes(subscription.status ?? '');
}

export function getSubscriptionProvider(subscription: TNavbarSubscription | null | undefined): TSubscriptionProvider {
    return subscription?.sub_id ? 'paddle' : 'lemonsqueezy';
}

export function getPaddleRetainSubscriptionId(subscription: TNavbarSubscription | null | undefined): string | null {
    if (getSubscriptionProvider(subscription) !== 'paddle') return null;
    return String(subscription?.sub_id);
}

export async function openNavbarCancellationFlow({
    subscription,
    addWarning,
    openLemonSqueezyModal,
    detectProfitwellBlock,
    openRetainCancellationFlow,
}: TNavbarCancellationOptions): Promise<TNavbarCancellationResult> {
    if (getSubscriptionProvider(subscription) !== 'paddle') {
        openLemonSqueezyModal();
        return 'lemonsqueezy-modal';
    }

    const paddleSubscriptionId = getPaddleRetainSubscriptionId(subscription);
    if (!paddleSubscriptionId) {
        addWarning(CANCEL_UNAVAILABLE_MESSAGE);
        return 'warning';
    }

    const isBlocked = await detectProfitwellBlock();
    if (isBlocked) {
        addWarning(PADDLE_BLOCKED_MESSAGE);
        return 'warning';
    }

    const success = await openRetainCancellationFlow(paddleSubscriptionId);
    if (!success) {
        addWarning(PADDLE_CANCEL_UNAVAILABLE_MESSAGE);
        return 'warning';
    }

    return 'paddle-retain';
}

async function defaultSignOut() {
    const {signOut} = await import('/src/api/services/supabase');
    await signOut();
}

async function defaultGetPaddlePortal(customerId: string) {
    const {getPaddlePortal} = await import('/src/api/users');
    return getPaddlePortal(customerId);
}

export function useNavbar(dependencies: TNavbarDependencies = {}) {
    const session = dependencies.session ?? useAppSession();
    const notifications = dependencies.notifications ?? useNotifications();
    const navbarRouter = dependencies.router ?? router;
    const windowRef = dependencies.windowRef ?? window;
    const trackEvent = dependencies.logEvent ?? logEvent;
    const checkProfitwellBlock = dependencies.detectProfitwellBlock ?? detectProfitwellBlock;
    const openRetainFlow = dependencies.openRetainCancellationFlow ?? openRetainCancellationFlow;
    const getPortal = dependencies.getPaddlePortal ?? defaultGetPaddlePortal;
    const signOut = dependencies.signOut ?? defaultSignOut;

    const {state} = session;
    const {auth, subscription} = toRefs(state);

    const isRedirecting = ref(false);
    const isCancellationModalOpen = ref(false);
    const isReferralDialogOpen = ref(false);

    const mainLink = computed(() => getNavbarMainLink(auth.value));
    const userAvatarText = computed(() => getNavbarAvatarText(auth.value));
    const isSubscriptionActive = computed(() => isNavbarSubscriptionActive(subscription.value));
    const referralButtonText = computed(() => getNavbarReferralButtonText(isSubscriptionActive.value));

    async function logOut() {
        await signOut();
        session.resetAuth();
        (windowRef as any).posthog?.reset?.();
        navbarRouter.push('/');
        windowRef.location.href = '/';
    }

    async function openCancellationModal() {
        trackEvent('button_cancel_subscription', 'navbar');
        await openNavbarCancellationFlow({
            subscription: subscription.value,
            addWarning: (message) => notifications.warning(message),
            openLemonSqueezyModal: () => {
                isCancellationModalOpen.value = true;
            },
            detectProfitwellBlock: checkProfitwellBlock,
            openRetainCancellationFlow: openRetainFlow,
        });
    }

    async function openManageSubscriptionModal() {
        trackEvent('button_manage_subscription', 'navbar');
        if (subscription.value?.customer_id) {
            isRedirecting.value = true;
            try {
                const response = await getPortal(subscription.value.customer_id);
                if (response?.customer_portal) {
                    windowRef.location.href = response.customer_portal;
                } else {
                    isRedirecting.value = false;
                }
            } catch {
                isRedirecting.value = false;
            }
        } else {
            isCancellationModalOpen.value = true;
        }
    }

    function openReferralDialog() {
        trackEvent('button_referral_open', 'navbar');
        isReferralDialogOpen.value = true;
    }

    return {
        auth,
        subscription,
        isRedirecting,
        isCancellationModalOpen,
        isReferralDialogOpen,
        mainLink,
        userAvatarText,
        isSubscriptionActive,
        referralButtonText,
        logOut,
        openCancellationModal,
        openManageSubscriptionModal,
        openReferralDialog,
        trackEvent,
    };
}
